// how-to-start/utils/template-slide-mappings.ts
//
// THE TEMPLATE'S OWN DESIGNS, in the shape the slide generator takes.
//
// Normally the funnel asks a model to choose a layout per outline item
// (/generate-slides-layouts), which is right when there is no template: the
// content decides the design. On a template path it is the wrong question
// entirely -- the user already chose the designs, by choosing the template,
// and a model picking its own produces a different deck that happens to have
// the same number of slides.
//
// A template's slides carry the design the generator wants:
//
//   settings.notionDesignSlideId "1f22ac33-8591-..."   -> slideDesignId
//
// The other half, `slideName`, is NOT the template's own slide name. It is a
// FAMILY var out of a fixed vocabulary -- "array-icon-3-cards",
// "timeline-icon-4-vertical", "chart-bar-5" -- which the generator looks the
// field structure up by. Sending "Future plans" fails the whole deck:
//
//   multi_select option "Future plans" not found for property "var"
//
// So the family is derived from what the slide IS, which is the same reading
// ./template-outline-seed already does, and the template's own design id is
// sent alongside it.
//
// WHAT EACH HALF IS FOR. The family decides the FIELD STRUCTURE the slide
// generator writes against -- how many headings, how many bodies, whether
// there is a chart -- and that is all it decides now: the deck is built by
// sending the template's own slide tree to be filled, so the design is the
// template's rather than the family's. See dotell-backend docpilot/service.js.
//
// The design id is sent alongside and is a fallback only. It cannot resolve on
// its own: a template slide carries a Notion uuid and the design lookup wants
// a Mongo _id.
//
// ALL OR NOTHING. If any slide is missing either field this returns null and
// the caller falls back to asking the model for the whole deck. Half a deck in
// the template's designs and half in a model's would look like a mistake, and
// it would be one.

import { templateOutlineSeed } from "./template-outline-seed";

export type TemplateSlideMapping = {
  slideName: string;
  slideDesignId: string;
};

/**
 * The family vars the generator accepts, by the shape they describe.
 *
 * Taken from the vocabulary the backend reports when given one it does not
 * know. Each family fixes a field structure -- how many headings, how many
 * bodies, whether there is a chart -- so the closest one for a slide is the
 * one with its shape and its item count.
 */
const FAMILY_BY_KIND: Record<string, (items: number) => string> = {
  cover: () => "cover-full-image-right",
  metrics: (n) => `array-numbers-${clamp(n, 3, 6)}`,
  timeline: (n) => `timeline-icon-${clamp(n, 3, 6)}-vertical`,
  tabs: (n) => `tabs-top-image-right-${clamp(n, 3, 5)}`,
  narrator: (n) => `narrator-image-right-${clamp(n, 3, 5)}`,
  carousel: (n) => `array-rectangle-${clamp(n, 3, 6)}`,
  chart: (n) => `chart-bar-${clamp(n, 3, 8)}`,
  table: (n) => `array-rectangle-${clamp(n, 3, 6)}`,
  "cards with icons": (n) => `array-icon-${clamp(n, 2, 8)}-cards`,
  cards: (n) => `array-rectangle-${clamp(n, 3, 6)}`,
  "text with an image": () => "sidebyside-rectangle-1-image-right",
  text: () => "centeredtext-none-1-dark",
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value || min, min), max);

/** The family var for a slide of this kind holding this many items. */
export const familyVarFor = (kind: string, items: number): string =>
  (FAMILY_BY_KIND[kind] ?? FAMILY_BY_KIND.text)(items);

type SlideSettings = { notionDesignSlideId?: unknown };

/**
 * The mapping for ONE slide on its own -- a slide picked from the library for
 * an outline row. Read the same way a template's slides are, so the family
 * and the fill agree about what it is.
 */
export const pickedSlideMapping = (slide: unknown): TemplateSlideMapping => {
  const story = { slides: [slide] };
  return (
    templateSlideMappings(story, templateOutlineSeed(story))?.[0] ?? {
      slideName: familyVarFor("text", 0),
      slideDesignId: TEMPLATE_SLIDE_DESIGN_ID,
    }
  );
};

/**
 * The design id sent for a slide that has none of its own.
 *
 * A Storydoc template's slides carry a Notion design id; a slide saved to the
 * library or one of the user's own documents does not. The id is only ever a
 * fallback -- the generator fills the slide's OWN tree, which travels with the
 * request -- so a slide without one is not a slide that cannot be filled. Asking
 * the layout model instead cost a call per deck and forty seconds of "Writing
 * your slides...", to arrive at designs that were then ignored.
 */
export const TEMPLATE_SLIDE_DESIGN_ID = "template-slide";

const str = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

/**
 * One mapping per slide of the template, or null when the template cannot
 * answer for all of them.
 *
 * `seed` is the same reading ./template-outline-seed produces, so the family
 * and the outline agree about what each slide is.
 */
export const templateSlideMappings = (
  story: unknown,
  seed: { kind: string; items: number }[],
): TemplateSlideMapping[] | null => {
  const slides = (story as { slides?: unknown })?.slides;
  if (!Array.isArray(slides) || slides.length === 0) return null;
  if (seed.length !== slides.length) return null;

  const mappings: TemplateSlideMapping[] = [];
  for (let index = 0; index < slides.length; index++) {
    const settings = ((slides[index] as { settings?: SlideSettings })
      ?.settings ?? {}) as SlideSettings;
    mappings.push({
      slideName: familyVarFor(seed[index].kind, seed[index].items),
      slideDesignId:
        str(settings.notionDesignSlideId) || TEMPLATE_SLIDE_DESIGN_ID,
    });
  }
  return mappings;
};
